import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { tap } from 'rxjs';
import { RepoSettingsService } from '../../../../../data/repositories/repo-settings.service';
import { BreezeEntity } from '../../../../../data/services/breeze/breeze.service';
import { AutoMeasureService } from '../../../../estimates/estimate-view/plans/auto-measure/auto-measure.service';
import { AutoMeasureType } from '../../../../estimates/estimate-view/plans/auto-measure/entities/auto-measure-types';
import { TakeOffMeasurement } from '../../../domain';
import { TakeOffAddOnService } from '../../../domain/takeoff-addon.service';
import { PitchChangedEventArgs } from '../../../take-off/pitch-dropdown.component';
import { TakeOffConfig } from '../domain';
import { TakeOffV2StoresService } from '../domain/apis/take-off-v2.stores';
import { TakeOffV2DrawingService } from '../take-off-v2-drawing.service';
import { TakeOffAdditionalActionsComponent } from './ui-take-off-additional-actions/take-off-additional-actions.component';
import { TakeOffDrawingActionsComponent } from './ui-take-off-drawing-actions/take-off-drawing-actions.component';

@Component({
    selector: 'app-take-off-actions',
    imports: [CommonModule, TakeOffDrawingActionsComponent, TakeOffAdditionalActionsComponent, TooltipModule, FormsModule],
    templateUrl: './take-off-actions.component.html',
    styleUrls: [
        '../../../../../shared/composable-styles/button.scss',
        '../../../../../shared/composable-styles/input.scss',
        './take-off-actions.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TakeOffActionsComponent {
    readonly #takeOffV2DrawingService = inject(TakeOffV2DrawingService);
    readonly #takeOffV2StoresService = inject(TakeOffV2StoresService);
    readonly #autoMeasureService = inject(AutoMeasureService);
    readonly takeOffAddOnService = inject(TakeOffAddOnService);
    readonly #settings = inject(RepoSettingsService);

    selectedMeasurement = input.required<TakeOffMeasurement | null>();
    takeOffConfig = input.required<TakeOffConfig>();
    planName = input.required<string>();
    selectedPlan = input.required<BreezeEntity>();
    hasDrawings = input.required<boolean>();
    showAutoMeasure = input<boolean>(true);
    isAutoSave = input<boolean>(true);
    isApplySelected = input<boolean>(false);

    configUpdated = output<TakeOffConfig>();
    removeAllChanges = output();
    onClose = output();
    undoLastDrawing = output();
    saveChanges = output();
    renamePlan = output<string>();

    takeOffInprogress = signal<boolean>(false);
    storeSelectedPlan = this.#takeOffV2StoresService.selectedPlan$;

    onPlanNameChange({ value, control }: NgModel) {
        const trimmedName = value?.trim();
        if (trimmedName === this.planName()) return;

        if (!trimmedName) {
            control.reset(this.planName());
            return;
        }

        this.renamePlan.emit(trimmedName);
    }

    public onShapeChanged(shape: string) {
        const config = this.takeOffConfig();
        if (!config) return;
        this.configUpdated.emit({ ...config, shape: shape, editable: false });
    }

    public onColorChanged(color: string) {
        const config = this.takeOffConfig();
        if (!config) return;
        this.#settings.TakeOffColor = color;
        this.configUpdated.emit({ ...config, color: color });
    }

    public onPitchChanged(pitch: PitchChangedEventArgs) {
        const config = this.takeOffConfig();
        if (!config) return;
        this.configUpdated.emit({ ...config, pitch: pitch.degrees, pitchRatio: pitch.ratio });
    }

    public onDeduct() {
        const config = this.takeOffConfig();
        if (!config) return;
        this.configUpdated.emit({ ...config, negative: !config.negative });
    }

    public onDepthChanged(depth: number) {
        const config = this.takeOffConfig();
        if (!config) return;
        this.configUpdated.emit({ ...config, depth: depth });
    }

    public onDepthUnitChanged(depthUnit: string) {
        const config = this.takeOffConfig();
        if (!config) return;
        this.configUpdated.emit({ ...config, depthUnit: depthUnit });
    }

    public onUndoLastDrawing() {
        this.#takeOffV2DrawingService.removeLastDrawing();
        this.undoLastDrawing.emit();
    }

    onRemoveAll() {
        this.removeAllChanges.emit();
    }

    close() {
        this.onClose.emit();
    }

    #autoMeasure(type: AutoMeasureType) {
        this.#autoMeasureService
            .autoMeasure(type, this.#takeOffV2StoresService.snapshotSelectedPlan?.ID_Plan)
            .pipe(
                tap((res) => {
                    if (res.length) this.onPointEditModeChanged();
                })
            )
            .subscribe();
    }

    onAutoMeasureFloor(type: AutoMeasureType) {
        return this.#autoMeasure(type);
    }

    onRequestAddOn() {
        this.takeOffAddOnService.getAddOn()?.subscribe(() => {
            this.takeOffAddOnService.hasAddOn.set(true);
        });
    }

    onPointEditModeChanged() {
        const config = this.takeOffConfig();
        if (!config) return;
        this.configUpdated.emit({ ...config, editable: true, shape: '' });
    }
}
